import { CINEMA_DAY_START_HOUR } from './cinema-day.ts';
import type { TimeZone } from './locale-helper';

/**
 * The cinema's time zone, and the day arithmetic in it: calendar days (midnight to midnight, what
 * the cinema app and the cinema websites list by since CIN-15254) and the 04:00 Cinema Day.
 *
 * Every time and date an operator or a guest sees belongs to the cinema, not to whoever is looking:
 * an agency in Lisbon exporting a Berlin programme must publish 20:15, not 19:15, and a dashboard
 * read from New York must show the foyer's clock. `Date` cannot express "00:00 in Europe/Berlin"
 * from another zone — `getHours`/`setDate` read and write the process zone — so everything below
 * goes through `Intl.DateTimeFormat` with an explicit `timeZone`, and a calendar day is carried as a
 * **day key** (`YYYY-MM-DD`), which names the same day in every zone.
 *
 * `cinema-day.ts` next door defines the 04:00 boundary, which signage, web, admin and distributor
 * still use in the process zone; its `calendarDay*InZone` wrappers delegate here. This module is for
 * the places that cannot assume the process runs in the cinema's zone.
 *
 * Dependency-free apart from `cinema-day.ts` and type-only imports, so `frontend/test` loads it
 * without node_modules — the reason the arithmetic lives here and not beside the React code.
 */

/** A calendar day, `YYYY-MM-DD`. Zone-free: the same string names the same day everywhere. */
export type DayKey = string;

/** What the resolver needs from a cinema — `CinemaDto` satisfies it; so does a bare `{ countryCode }`. */
export type CinemaZoneSource = { timezone?: string | null; countryCode?: string | null };

/**
 * The zone a country's cinemas run on, for cinemas whose own `timezone` is not set. All three share
 * one offset year-round, so this changes no rendered time today — it is here so the first cinema
 * outside CET fails as a missing entry rather than as silently wrong times.
 */
export const COUNTRY_TIME_ZONE: Record<string, TimeZone> = {
    DE: 'Europe/Berlin',
    AT: 'Europe/Vienna',
    CH: 'Europe/Zurich'
};

export const DEFAULT_TIME_ZONE: TimeZone = 'Europe/Berlin';

/**
 * The zone every time shown for this cinema is rendered in: the cinema's own `timezone` first
 * (IANA, from the API), its country's zone second, CET last. `undefined` while the cinema is still
 * loading resolves to the fallback, so a caller can compute before it knows the cinema.
 */
export function cinemaTimeZone(cinema?: CinemaZoneSource | null): TimeZone {
    // `timezone` is typed as a plain string on the DTO; the API writes IANA names into it.
    return (
        (cinema?.timezone as TimeZone | undefined) || COUNTRY_TIME_ZONE[cinema?.countryCode ?? ''] || DEFAULT_TIME_ZONE
    );
}

/**
 * The zone a locale implies — `de-AT` is Vienna, `de-DE` and `de` are Berlin.
 *
 * For surfaces that have no single cinema to ask: cineamo.com spans every cinema at once, so the
 * country in the URL locale is the only thing that says which country's clock the page is about.
 * Reads the same {@link COUNTRY_TIME_ZONE} table as {@link cinemaTimeZone}, so the two can never
 * disagree about a country.
 *
 * `en-US` and `en-GB` are the English *interface* of the same German-market platform, not a US or UK
 * market, so they land on the fallback rather than on a US/UK zone — which is what we want, and the
 * reason this maps the country to a zone rather than trusting the language.
 */
export function localeTimeZone(countryCode?: string | null): TimeZone {
    return COUNTRY_TIME_ZONE[countryCode ?? ''] || DEFAULT_TIME_ZONE;
}

const DAY_KEY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

type WallClock = { year: number; month: number; day: number; hour: number; minute: number; second: number };

/**
 * One formatter per zone. Constructing an `Intl.DateTimeFormat` costs far more than formatting with
 * one, and the program page reads the wall clock once per showing per render — rebuilding it on
 * every call was the largest single cost of rendering that page. The set of zones is tiny (the
 * cinemas' own), so the map never needs evicting.
 */
const wallClockFormats = new Map<string, Intl.DateTimeFormat>();

function wallClockFormat(timeZone: string): Intl.DateTimeFormat {
    let format = wallClockFormats.get(timeZone);
    if (!format) {
        // `hourCycle: 'h23'`, not `hour12: false`: some engines render midnight as "24" under the latter.
        format = new Intl.DateTimeFormat('en-US', {
            timeZone,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        wallClockFormats.set(timeZone, format);
    }
    return format;
}

/** What a clock on the wall in `timeZone` reads at `instant`. */
function wallClock(instant: Date, timeZone: string): WallClock {
    const parts = wallClockFormat(timeZone).formatToParts(instant);
    const read = (type: Intl.DateTimeFormatPartTypes): number =>
        Number(parts.find((part) => part.type === type)?.value);
    return {
        year: read('year'),
        month: read('month'),
        day: read('day'),
        hour: read('hour'),
        minute: read('minute'),
        second: read('second')
    };
}

function formatDayKey(year: number, month: number, day: number): DayKey {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Whether `value` is a day key — the form a URL parameter or an API date field must take to be read as one. */
export function isDayKey(value: string): value is DayKey {
    return DAY_KEY_PATTERN.test(value);
}

function parseDayKey(dayKey: DayKey): { year: number; month: number; day: number } {
    const match = DAY_KEY_PATTERN.exec(dayKey);
    if (!match) {
        throw new RangeError(`Not a day key (YYYY-MM-DD): ${dayKey}`);
    }
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

/** The calendar day `instant` falls on in `timeZone`. */
export function zonedDayKey(instant: Date, timeZone: string): DayKey {
    const { year, month, day } = wallClock(instant, timeZone);
    return formatDayKey(year, month, day);
}

/** Midnight UTC of the day; `Date.UTC` reads a year below 100 as 19xx, which a half-typed date field produces (0002-11-01). */
const utcDay = (year: number, month: number, day: number): Date => {
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    return date;
};

/**
 * `dayKey` plus `days` calendar days (negative to step back). Pure calendar arithmetic — a day key is
 * already a calendar day, so no zone enters here; DST cannot lose or gain a day because no instant
 * is involved. The zone matters only where a key meets an instant (`calendarDayStartInZone`).
 */
export function addCalendarDays(dayKey: DayKey, days: number): DayKey {
    const { year, month, day } = parseDayKey(dayKey);
    const stepped = utcDay(year, month, day + days);
    return formatDayKey(stepped.getUTCFullYear(), stepped.getUTCMonth() + 1, stepped.getUTCDate());
}

/** Calendar days from `fromKey` to `toKey`, negative when `toKey` is earlier. */
export function calendarDaysBetween(fromKey: DayKey, toKey: DayKey): number {
    const from = parseDayKey(fromKey);
    const to = parseDayKey(toKey);
    const span = utcDay(to.year, to.month, to.day).getTime() - utcDay(from.year, from.month, from.day).getTime();
    return Math.round(span / (24 * 60 * 60 * 1000));
}

/** How far `timeZone`'s wall clock is ahead of UTC at `instant`, in milliseconds (negative west of Greenwich). */
export function zoneOffsetMs(instant: Date, timeZone: string): number {
    const wall = wallClock(instant, timeZone);
    const wallAsUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    return wallAsUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * The instant at which the wall clock in `timeZone` reads `hour`:`minute`:00.000 on `dayKey`.
 *
 * Two passes: the offset in force at the naive guess (the wall time read as UTC) can differ from the
 * offset in force at the answer when a DST transition lies between the two, so the second pass reads
 * the offset at the first answer. Converges for every wall time that exists on that day. A time that
 * repeats (the autumn night) resolves to its second occurrence; one the spring night skips lands at or
 * after the jump, an hour on (Berlin 2026-03-29: 02:00 → 01:00Z, the jump itself; 02:30 → 01:30Z).
 */
export function zonedInstant(dayKey: DayKey, hour: number, timeZone: string, minute = 0): Date {
    const { year, month, day } = parseDayKey(dayKey);
    const wallAsUtc = Date.UTC(year, month - 1, day, hour, minute);
    const firstPass = wallAsUtc - zoneOffsetMs(new Date(wallAsUtc), timeZone);
    return new Date(wallAsUtc - zoneOffsetMs(new Date(firstPass), timeZone));
}

/**
 * Midnight in `timeZone` on `dayKey` — the start of that calendar day there, as an instant.
 *
 * The guest-facing surfaces (the cinema website, the cinema app's own listings) file a showing under
 * the calendar day it starts on, not under the Cinema Day: a 01:00 screening is listed on the day its
 * clock reads, which is what a guest reading a date filter expects (CIN-15254). The Cinema Day
 * remains in use wherever a cinema's *business* day is meant — closures, occupancy — so both
 * boundaries live here side by side.
 */
export function calendarDayStartInZone(dayKey: DayKey, timeZone: string): Date {
    return zonedInstant(dayKey, 0, timeZone);
}

/**
 * The last instant of the calendar day `dayKey` names in `timeZone` — 23:59:59.999 there.
 *
 * Derived from the *next* day's midnight rather than by setting 23:59:59.999 directly, so a day that
 * is 23 or 25 hours long across a DST transition still ends where the next one begins.
 */
export function calendarDayEndInZone(dayKey: DayKey, timeZone: string): Date {
    return new Date(calendarDayStartInZone(addCalendarDays(dayKey, 1), timeZone).getTime() - 1);
}

/** 04:00 in `timeZone` on the calendar day `dayKey` names — the start of that Cinema Day, as an instant. */
export function cinemaDayStartInZone(dayKey: DayKey, timeZone: string): Date {
    return zonedInstant(dayKey, CINEMA_DAY_START_HOUR, timeZone);
}

/** The last instant of the Cinema Day `dayKey` names in `timeZone` — 03:59:59.999 the next morning there. */
export function cinemaDayEndInZone(dayKey: DayKey, timeZone: string): Date {
    return new Date(cinemaDayStartInZone(addCalendarDays(dayKey, 1), timeZone).getTime() - 1);
}

/**
 * The Cinema Day `instant` falls inside in `timeZone`, which before 04:00 there is still the previous
 * calendar day's. The zone-aware form of `cinemaDayOf` in `cinema-day.ts`.
 */
export function cinemaDayKeyOf(instant: Date, timeZone: string): DayKey {
    const calendarDay = zonedDayKey(instant, timeZone);
    return instant.getTime() < cinemaDayStartInZone(calendarDay, timeZone).getTime()
        ? addCalendarDays(calendarDay, -1)
        : calendarDay;
}

/**
 * Midnight in the *process* zone on `dayKey` — a `Date` that names the day for code running in the
 * viewer's zone: a date picker, or a formatter without an explicit zone. Never send it to the API
 * or compare it with an instant; it is a label, not a moment.
 */
export function localDateOfDayKey(dayKey: DayKey): Date {
    const { year, month, day } = parseDayKey(dayKey);
    return new Date(year, month - 1, day);
}

/** The calendar day a process-zone `Date` names — the inverse of `localDateOfDayKey`, for what a date picker hands back. */
export function dayKeyOfLocalDate(date: Date): DayKey {
    return formatDayKey(date.getFullYear(), date.getMonth() + 1, date.getDate());
}
